import {
  useState,
  useImperativeHandle,
  useEffect,
  Ref,
  forwardRef,
  memo,
  useMemo,
  useRef,
} from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { WindupChildren, Pace } from 'windups';
import { Blink, Loading } from './Blink';
import { useLatest, useMemoizedFn } from 'ahooks';
import { unstable_batchedUpdates } from 'react-dom';
import { useEventSource } from '../hooks/useEventSource';
import type { Control } from '../utils/sseRenderControl';
import { PACEMS } from '../consts/paceMs';
import { text2mustache } from '@knowledge/Activity/PolicyForm/share';
import { useChunkRender } from './Reuse/ChunkRender';
import { getChunks } from 'views/Aide/Scenario/PolicyTable';
import { Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';

const blink = <Blink />;

export type SSEStatus = 'Idle' | 'Pending' | 'Done' | 'Error';

export interface SSERenderProps extends React.ComponentPropsWithoutRef<'div'> {
  defaultValue?: string;
  autoRun?: boolean;
  path?: string;
  query: Record<string, string>;
  onDone?: (value: string) => void;
  onError?: () => void;
  control?: Control; // 这是因为windupChildren不能传递ref，所以只能通过这种方式传递control
  chunkProps?: {
    platform: General.PlatformUpperCase;
    region: General.UpperCaseRegion;
  };
  hasItem?: boolean;
  hasVoucher?: boolean;
  retry?: number;
  onSSEStatusChange?: (status: SSEStatus) => void;
}

export interface SSERenderRef {
  run: () => EventSourcePolyfill | undefined;
  getSSEStatus: () => SSEStatus | undefined;
  getContent: () => string;
  updateContent: (value: string) => void;
}

const SSERenderComponent = (props: SSERenderProps, ref: Ref<SSERenderRef>) => {
  const {
    autoRun = true,
    defaultValue = '',
    path,
    query,
    onDone,
    onError,
    control,
    chunkProps,
    hasItem,
    hasVoucher,
    retry,
    onSSEStatusChange,
    ...restProps
  } = props;

  const { t } = useTranslate();

  const [str, setStr] = useState<string>(defaultValue);
  const [cursor, setCursor] = useState<[number, number]>([0, 0]);
  const cursorRef = useLatest(cursor);
  useEffect(() => {
    if (cursorRef.current[1] === 0) {
      setCursor([0, str.length]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [str, setCursor]);
  const effectStr = useMemo(() => str.slice(0, cursor[0]), [str, cursor]);
  const renderStr = useMemo(() => str.slice(cursor[0], cursor[1]), [str, cursor]);
  const strRef = useLatest(str);
  const [isFulfilled, setIsFulfilled] = useState(true);
  const [sseStatus, setSSEStatus] = useState<SSEStatus>(defaultValue ? 'Pending' : 'Idle');
  const getSource = useEventSource(path);

  const isChunk = Boolean(chunkProps);
  const mustache = useMemo(
    () =>
      isChunk
        ? str.startsWith('#mustache#')
          ? str
          : text2mustache(str, {
              item: hasItem,
              voucher: hasVoucher,
            })
        : '',
    [isChunk, str, hasItem, hasVoucher],
  );
  const chunks = useMemo(
    () => (isChunk ? getChunks({ content: mustache, translatedContent: '' }) : []),
    [isChunk, mustache],
  );
  const chunksContent = useChunkRender({
    chunks,
    platform: chunkProps?.platform || 'SHOPEE',
    region: chunkProps?.region || 'ID',
    textExtra: sseStatus === 'Pending' && blink,
    language: chunkProps?.region ? regionLanguageMap[toLowerCase(chunkProps.region)] : undefined,
  });

  const runSSE = useMemoizedFn((retry = 0) => {
    if (sseStatus === 'Pending') return;

    unstable_batchedUpdates(() => {
      setStr('');
      setCursor([0, 0]);
      setSSEStatus('Idle');
      setIsFulfilled(false);
    });

    const source = getSource(query);
    source.addEventListener('message', function (event) {
      try {
        const data: any = JSON.parse(event.data);
        if (data.ops === 'generating') {
          unstable_batchedUpdates(() => {
            setSSEStatus('Pending');
            setStr((str) => {
              return str + data.content;
            });
          });
        }
        if (data.ops === 'done') {
          source.close();
          // 防止动画抖动
          requestAnimationFrame(() => {
            setIsFulfilled(true);
          });
        }
      } catch (e) {
        console.log(e);
      }
    });
    source.addEventListener('error', function (event) {
      console.error(event);
      source.close();
      onError?.();
      if (retry) {
        runSSE(retry - 1);
      } else {
        setSSEStatus('Error');
      }
    });

    return source;
  });

  useImperativeHandle(ref, () => ({
    run: runSSE,
    getContent: () => str,
    updateContent: setStr,
    getSSEStatus: () => sseStatus,
  }));

  useEffect(() => {
    let source: EventSourcePolyfill | undefined;
    if (autoRun && !defaultValue) {
      source = runSSE(retry);
    }
    return () => {
      source?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (control) {
      control.emitter.on('run', () => {
        runSSE();
      });
      control.emitter.on('getValue', () => {
        control.emitter.emit('receiveValue', strRef.current);
      });
      control.emitter.on('updateContent', (value) => {
        setStr(value as string);
        onDone?.(value as string);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refsCurrent = {
    onSSEStatusChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    refs.current.onSSEStatusChange?.(sseStatus);
  }, [sseStatus]);

  const strContent = isChunk ? chunksContent : str;
  const renderStrContent = isChunk ? chunksContent : renderStr;
  const pendingCondition = isChunk ? isFulfilled && sseStatus !== 'Done' : sseStatus === 'Pending';
  const loadingCondition = isChunk ? !isFulfilled : sseStatus === 'Idle';

  return (
    <div {...restProps} style={{ minHeight: 26 }}>
      {pendingCondition && (
        <>
          {effectStr}
          <WindupChildren
            onFinished={() => {
              if (!isChunk) {
                setCursor((prev) => {
                  return [prev[1], str.length];
                });
              }
              if (isChunk ? isFulfilled : isFulfilled && cursor[1] === str.length) {
                setSSEStatus('Done');
                control?.emitter?.emit('contentChange', strRef.current);
                onDone?.(isChunk ? mustache : str);
              }
            }}
          >
            <Pace ms={PACEMS}>{renderStrContent}</Pace>
          </WindupChildren>
          {!isChunk && <Blink />}
        </>
      )}
      {sseStatus === 'Done' && <div className="shit-it-is">{strContent}</div>}
      {loadingCondition && <Loading />}
      {sseStatus === 'Error' && (
        <div>
          {strContent} <span className="px-2 text-error">{t('生成失败')}</span>
          <Button type="link" size="small" onClick={runSSE}>
            {t('gpt_renew_content')}
          </Button>
        </div>
      )}
    </div>
  );
};

export const SSERender = memo(forwardRef(SSERenderComponent));
